import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import season from '../../canon/season.json';
import { methodDisplay, summarizeReadiness } from '../../services/chargeTarget';
import {
	generateInvoices,
	chargeTuitionInvoices,
	subscribeToInvoicesForPeriod,
	recordBillingPayment,
	adjustBillingLedger,
	voidInvoice,
	reconcileBillingBalance,
	enrollStudentForSeason,
	withdrawTuitionEnrollment,
	subscribeToInvoices,
	subscribeToLedger,
	subscribeToEnrollments,
	subscribeToFamilyStudents,
	allocatePayments,
	formatCents,
	formatPeriodMonth,
	formatDateOnly,
	describeLedgerEntry,
} from '../../services/billingLedgerService';

/* Work → Billing (ADMIN/OWNER ONLY — route-gated, and every callable behind it
 * re-checks staff on the server).
 *
 * THE ADMIN HALF OF AUTOPAY SLICE 2. Nothing here charges anything: it issues
 * statements, records payments that happened elsewhere, and corrects mistakes.
 *
 * WHY "RECORD PAYMENT" IS THE MOST IMPORTANT CONTROL ON THIS PAGE, TODAY.
 * Until charging ships, an invoice generated here is collected by hand — check,
 * cash, or a manual Stripe charge — and the ledger only tells the truth if
 * those payments are entered. Without that, a family opens the portal in
 * September, sees an invoice and no payment, and concludes we lost their money.
 *
 * THERE IS NO MMS METHOD, AND THAT IS A BUSINESS FACT RATHER THAN AN OVERSIGHT
 * (Anthony, 2026-08-02). This list once led with `mms`, on the theory that MMS
 * would keep charging cards while the portal issued the statements. It will
 * not: MMS is abandoned outright at the Aug 31 cutover, and every invoice this
 * page can generate is school-year tuition, which starts in September — so a
 * portal invoice and an MMS charge never overlap by a single day. Removed
 * while `ledgerEntries` was still empty studio-wide, so no stored entry carries
 * `method: 'mms'`. NOTE that the server enum in `functions/billingLedger.js`
 * still ACCEPTS 'mms': that is deliberate, since a permissive server outliving
 * a narrowed UI is the safe direction and not the unsafe one. Do not read its
 * presence there as this list being incomplete.
 *
 * GENERATION IS DRY-RUN-FIRST AND THE PREVIEW IS THE PLAN. The button that
 * writes is deliberately a second, differently-worded action that only appears
 * after a preview has been read — core.md's standing rule for any batch
 * operation, and the one place on this page where a misclick bills every
 * family in the studio.
 */

const MONTH_OPTIONS = (() => {
	// Derived from canon, not hardcoded — same source the generator validates
	// against, so the picker cannot offer a month the server will refuse.
	const label = season.seasonLabel || '';
	const startYear = Number(String(label).slice(0, 4));
	const names = [
		'january', 'february', 'march', 'april', 'may', 'june',
		'july', 'august', 'september', 'october', 'november', 'december',
	];
	const firstIdx = names.indexOf(String(season.tuition?.firstPaymentMonth || '').toLowerCase());
	const count = season.tuition?.installments || 0;
	if (!Number.isFinite(startYear) || firstIdx < 0 || !count) return [];
	return Array.from({ length: count }, (_, i) => {
		const idx = firstIdx + i;
		const year = startYear + Math.floor(idx / 12);
		return `${year}-${String((idx % 12) + 1).padStart(2, '0')}`;
	});
})();

const PAYMENT_METHODS = [
	{ value: 'check', label: 'Check' },
	{ value: 'cash', label: 'Cash' },
	{ value: 'stripe_manual', label: 'Stripe (manual)' },
	{ value: 'other', label: 'Other' },
];

const ADJUSTMENT_REASONS = [
	{ value: 'goodwill', label: 'Goodwill' },
	{ value: 'sibling_discount', label: 'Sibling discount' },
	{ value: 'scholarship', label: 'Scholarship' },
	{ value: 'prorated_start', label: 'Prorated start' },
	{ value: 'withdrawal_settlement', label: 'Withdrawal settlement' },
	{ value: 'admin_correction', label: 'Correction' },
];

// `--surface-card`, NOT `--surface-raised`. In the portal theme `--surface-raised`
// is #384971 — the navy CHROME colour used by the sidebar and topbar — so a card
// painted with it renders navy with #333 body text on top: legible in jsdom
// (which computes no colour at all) and unreadable on a screen. The `#fff`
// fallback beside it is no protection, because a fallback only applies when the
// variable is UNDEFINED, not when it is defined to the wrong thing.
// Plain-language labels for the charge planner's skip reasons and the run's
// outcomes. Falling back to the raw code (rather than hiding an unknown value)
// is deliberate: a new server-side reason must surface, not vanish.
// Generation skips, named for the same reason charge skips are: a COUNT
// ("2 already exist and will be skipped") tells an admin that something was
// left out but not who or why, and both known ways to be skipped here are
// states someone needs to act on rather than shrug at.
const GENERATE_SKIP_LABELS = {
	already_exists: 'already has a live invoice for this month',
	on_hold: 'on hold — not auto-invoiced',
};

const SKIP_LABELS = {
	already_charged: 'already charged',
	charge_pending: 'bank debit already in flight',
	external_rail: 'pays outside Stripe — never charged',
	no_method: 'no payment method on file',
	covered_by_credit: 'covered by account credit',
};
const OUTCOME_LABELS = {
	succeeded: 'Charged',
	processing: 'Bank debit in flight',
	failed: 'FAILED',
	already_charged: 'Already charged',
	charge_pending: 'Bank debit already in flight',
	external_rail: 'Pays outside Stripe',
	no_method: 'No payment method',
	covered_by_credit: 'Covered by credit',
};

const cardStyle = {
	border: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
	borderRadius: 'var(--radius-md, 10px)',
	padding: '1.1rem 1.15rem',
	marginBottom: '1.25rem',
	background: 'var(--surface-card, #fff)',
	color: 'var(--text-body, #333)',
};
const th = { padding: '0.5rem 0.4rem', textAlign: 'left' };
const td = { padding: '0.55rem 0.4rem' };

/* A sortable column header.
 *
 * TWO REPO TRAPS ARE BEING PAID OFF HERE ON PURPOSE — read before simplifying.
 *
 * 1. `native.css` sizes every bare <button> and wins BY LAYER, not specificity:
 *    it sets `height: var(--wa-form-control-height)` (30px, theme-dependent)
 *    and `padding: 0`. A more specific selector does not beat a layer — only
 *    DECLARING the property does. Left alone it would inflate the header row
 *    and crush the label's padding inside a fixed 30px box. Hence the explicit
 *    height/padding/font below. This is the same defect that hit the Season
 *    calendar and the Schedule page's Cancel button.
 *
 * 2. THE CARET IS ALWAYS RENDERED, never revealed on hover. A hover-only
 *    affordance fails twice here: the portal's primary user has keratoconus,
 *    for whom a control that appears only on hover is the hardest possible
 *    thing to find, and the installed PWA has no hover at all. Inactive
 *    columns are quieted with COLOUR, not opacity — and `--text-faint` is not
 *    declared anywhere in this repo, so it must carry a fallback or the
 *    declaration is invalid at computed-value time and the element silently
 *    INHERITS full-strength colour.
 */
const SortHeader = ({ label, columnKey, sort, onSort, align = 'left' }) => {
	const active = sort.key === columnKey;
	return (
		<th style={{ ...th, textAlign: align }}>
			<button
				type='button'
				onClick={() => onSort(columnKey)}
				aria-label={`Sort by ${label}${active ? (sort.dir === 'asc' ? ', currently ascending' : ', currently descending') : ''}`}
				style={{
					height: 'auto',
					padding: 0,
					background: 'none',
					border: 0,
					font: 'inherit',
					fontWeight: active ? 700 : 600,
					color: 'inherit',
					cursor: 'pointer',
					display: 'inline-flex',
					alignItems: 'center',
					gap: '0.35rem',
				}}>
				{label}
				<i
					aria-hidden='true'
					className={
						active
							? sort.dir === 'asc'
								? 'fa-sharp fa-solid fa-arrow-up-short-wide'
								: 'fa-sharp fa-solid fa-arrow-down-wide-short'
							: 'fa-sharp fa-solid fa-sort'
					}
					style={{
						fontSize: '0.75em',
						color: active ? 'inherit' : 'var(--text-faint, var(--text-muted, #7b8499))',
					}}
				/>
			</button>
		</th>
	);
};

/**
 * Dollars typed by a human -> integer cents.
 *
 * Parsed here rather than sent as a float: the server takes CENTS and refuses
 * anything else, so the conversion has exactly one home on each side. Returns
 * null for anything unparseable so the caller can refuse rather than send a
 * NaN that the server would have to interpret.
 */
function parseDollarsToCents(text) {
	const trimmed = String(text || '').trim().replace(/^\$/, '').replace(/,/g, '');
	if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
	return Math.round(Number(trimmed) * 100);
}

const Billing = () => {
	const [families, setFamilies] = useState([]);
	const [profiles, setProfiles] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const [periodMonth, setPeriodMonth] = useState(MONTH_OPTIONS[0] || '');
	// DECLARED HERE, NOT BESIDE THE CHARGE CARD BELOW, AND THAT IS LOAD-BEARING.
	// The families header reads this month, and its useMemo sits above the
	// charge card. Babel transpiles `const` to `var` for this project's browser
	// targets, which removes the temporal dead zone — so a read from above
	// would silently yield `undefined` instead of throwing, and the header
	// would quietly scope itself to no month at all. See CLAUDE.md → Dates &
	// browser environment.
	const [chargePeriod, setChargePeriod] = useState(MONTH_OPTIONS[0] || '');
	// Every invoice in the selected charge month, all statuses (see
	// subscribeToInvoicesForPeriod for why the status filter is client-side).
	const [periodInvoices, setPeriodInvoices] = useState([]);
	const [preview, setPreview] = useState(null);
	const [runResult, setRunResult] = useState(null);

	const [selectedFamilyId, setSelectedFamilyId] = useState(null);

	useEffect(() => {
		const unsubFamilies = onSnapshot(
			collection(db, 'families'),
			(snap) => {
				setFamilies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
				setLoading(false);
			},
			(err) => {
				console.error('[billing-admin] families:', err);
				setError('Could not load families.');
				setLoading(false);
			}
		);
		const unsubProfiles = onSnapshot(
			collection(db, 'billingProfiles'),
			(snap) => {
				const next = {};
				snap.docs.forEach((d) => {
					next[d.id] = d.data();
				});
				setProfiles(next);
			},
			(err) => console.error('[billing-admin] profiles:', err)
		);
		return () => {
			unsubFamilies();
			unsubProfiles();
		};
	}, []);

	// Re-subscribes when the admin changes the charge month, so the families
	// header always describes the month the Charge control is pointed at.
	useEffect(
		() => subscribeToInvoicesForPeriod(chargePeriod, setPeriodInvoices),
		[chargePeriod]
	);

	// Sort state for the families table.
	//
	// DEFAULT STAYS BALANCE-DESCENDING, deliberately: the list exists to be
	// worked through and alphabetical order buries the families that need
	// attention. What was missing was any way to CHANGE it — with ~56 families
	// and no search, finding one specific family meant scanning a list ordered
	// by a number you don't know.
	const [sort, setSort] = useState({ key: 'balance', dir: 'desc' });

	// Clicking the active column flips direction; a new column adopts the
	// direction that is USEFUL FIRST for that column rather than a fixed
	// default — name wants A-Z, balance wants who-owes-most, method wants
	// who-still-has-none. Getting a useless order on the first click and
	// having to click again is a small tax paid every single time.
	const toggleSort = useCallback((key) => {
		setSort((s) =>
			s.key === key
				? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
				: { key, dir: key === 'name' ? 'asc' : 'desc' }
		);
	}, []);

	const rows = useMemo(() => {
		const mapped = families.map((f) => ({
			...f,
			balanceCents: profiles[f.id]?.balanceCents ?? null,
			// The Method column and the readiness count both come from
			// methodDisplay(), which mirrors the SERVER's resolveChargeTarget.
			// The old predicate here was Boolean(profile.paymentMethod), which
			// silently ignored `chargePaymentMethod` — the mapped MMS-season
			// card the charge run falls back to — and so reported 9/59 ready on
			// a night the run could bill 28 of 32. See src/services/chargeTarget.js.
			method: methodDisplay(profiles[f.id]),
			autopayStatus: profiles[f.id]?.autopayStatus || 'none',
		}));
		const name = (r) => String(r.primaryGuardianName || r.id || '').toLowerCase();
		// A null balance means "no billing profile yet", not "owes nothing" —
		// but for ordering it sits at zero, which is where it belongs among
		// families who owe something and families who are square.
		const bal = (r) => r.balanceCents || 0;
		// Sorts to who-needs-chasing first under 'desc': none, then verifying,
		// then external, then chargeable. That ordering is the Sept 1 work
		// queue. The rank lives on methodDisplay() so the column and the count
		// can never disagree about what "has a method" means.
		const method = (r) => r.method.rank;

		const cmp = {
			name: (a, b) => name(a).localeCompare(name(b)),
			balance: (a, b) => bal(a) - bal(b),
			method: (a, b) => method(a) - method(b),
		}[sort.key];

		const sign = sort.dir === 'desc' ? -1 : 1;
		// The direction is applied to the PRIMARY comparator only. Reversing the
		// finished array instead would also reverse the tie-break, so every
		// group of equal values would flip to Z-A — which reads as a bug on the
		// balance column, where most families share a balance of 0.
		return [...mapped].sort((a, b) => {
			const primary = cmp(a, b) * sign;
			return primary !== 0 ? primary : name(a).localeCompare(name(b));
		});
	}, [families, profiles, sort]);

	const totals = useMemo(() => {
		const outstanding = rows.reduce((s, r) => s + Math.max(0, r.balanceCents || 0), 0);
		return { outstanding };
	}, [rows]);

	// READINESS FOR THE SELECTED CHARGE MONTH — scoped to the families who
	// actually have an invoice in it, not to the whole roster.
	//
	// The roster denominator was the second half of the same defect as the
	// `paymentMethod` predicate: on 2026-08-31 the header read "9/59 with a
	// payment method" when the September run could bill 28 of the 32 families
	// it had invoiced. Fixing the predicate alone moved that to 28/59, which is
	// still the wrong question — the 27 families with no September invoice are
	// not un-ready, they are not being billed. A number a human reads before
	// moving money should describe the run that is about to happen.
	const readiness = useMemo(
		() => summarizeReadiness(periodInvoices, profiles),
		[periodInvoices, profiles]
	);

	const runPreview = useCallback(async () => {
		setBusy(true);
		setError('');
		setRunResult(null);
		try {
			setPreview(await generateInvoices({ periodMonth, dryRun: true }));
		} catch (err) {
			console.error('[billing-admin] preview failed:', err);
			setError(err.message || 'Could not preview.');
		} finally {
			setBusy(false);
		}
	}, [periodMonth]);

	const runForReal = useCallback(async () => {
		setBusy(true);
		setError('');
		try {
			const res = await generateInvoices({ periodMonth, dryRun: false });
			setRunResult(res);
			setPreview(null);
		} catch (err) {
			console.error('[billing-admin] generation failed:', err);
			setError(err.message || 'Could not generate invoices.');
		} finally {
			setBusy(false);
		}
	}, [periodMonth]);

	// ── Charging (autopay slice 3) ─────────────────────────────────────────
	// Separate month + busy state from the Generate card on purpose: previewing
	// a charge run must not clear an invoice preview someone is reading, and
	// the two actions must never share a button's loading spinner.
	const [chargePlan, setChargePlan] = useState(null);
	const [chargeResult, setChargeResult] = useState(null);
	const [chargeBusy, setChargeBusy] = useState(false);

	const previewCharges = useCallback(async () => {
		setChargeBusy(true);
		setError('');
		setChargeResult(null);
		try {
			setChargePlan(await chargeTuitionInvoices({ periodMonth: chargePeriod, dryRun: true }));
		} catch (err) {
			console.error('[billing-admin] charge preview failed:', err);
			setError(err.message || 'Could not preview the charge run.');
		} finally {
			setChargeBusy(false);
		}
	}, [chargePeriod]);

	const executeCharges = useCallback(async () => {
		// Guard the handler, not only the button — a wa-* `disabled` is applied
		// as a property after upgrade, so a click can land before it takes
		// (CLAUDE.md, found 2026-08-13).
		if (chargeBusy || !chargePlan || chargePlan.counts.toCharge === 0) return;
		const total = formatCents(chargePlan.totalChargeCents, { showCredit: false });
		// The one irreversible action on this page. window.confirm is the house
		// pattern (ManageAnnouncements delete) and the text names the money.
		if (
			!window.confirm(
				`Charge ${chargePlan.counts.toCharge} famil${
					chargePlan.counts.toCharge === 1 ? 'y' : 'ies'
				} a total of ${total} for ${formatPeriodMonth(chargePlan.periodMonth)}? ` +
					'Cards are debited immediately; bank accounts start a debit that settles over days.'
			)
		) {
			return;
		}
		setChargeBusy(true);
		setError('');
		try {
			const res = await chargeTuitionInvoices({ periodMonth: chargePeriod, dryRun: false });
			setChargeResult(res);
			setChargePlan(null);
		} catch (err) {
			console.error('[billing-admin] charge run failed:', err);
			setError(err.message || 'The charge run failed to start.');
		} finally {
			setChargeBusy(false);
		}
	}, [chargeBusy, chargePlan, chargePeriod]);

	return (
		<>
			{/* The subtitle used to say "Nothing here charges a card." That was a
			    load-bearing sentence while it was true; the charge run below
			    retired it on 2026-08-24. */}
			<PortalHeader
				title='Billing'
				subtitle='Invoices, payments, balances — and the monthly charge run.'
			/>
			<div style={{ padding: '1.25rem 1rem 3rem', maxWidth: 1100, margin: '0 auto' }}>
				{error && (
					<wa-callout variant='danger' style={{ marginBottom: '1rem' }}>
						<i slot='icon' className='fa-sharp fa-solid fa-triangle-exclamation'></i>
						{error}
					</wa-callout>
				)}

				{/* Standing reminder, not decoration: this page looks exactly like
				    one that takes money, and the difference matters most to whoever
				    reads it in a hurry. */}
				<wa-callout variant='neutral' style={{ marginBottom: '1.25rem' }}>
					<i slot='icon' className='fa-sharp fa-solid fa-circle-info'></i>
					Generating an invoice here records what a family owes — it does not
					bill them.
				</wa-callout>

				{/* ── Generate ────────────────────────────────────────────────── */}
				<div style={cardStyle}>
					<h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', margin: '0 0 0.8rem' }}>
						Generate invoices
					</h2>
					<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
						<select
							value={periodMonth}
							onChange={(e) => {
								setPeriodMonth(e.target.value);
								setPreview(null);
								setRunResult(null);
							}}
							style={{ padding: '0.45rem 0.6rem', borderRadius: 8 }}>
							{MONTH_OPTIONS.map((m) => (
								<option key={m} value={m}>
									{formatPeriodMonth(m)}
								</option>
							))}
						</select>
						<WaButton appearance='outlined' loading={busy} disabled={busy} onClick={runPreview}>
							Preview
						</WaButton>
					</div>

					{preview && (
						<div style={{ marginTop: '1rem' }}>
							<p style={{ margin: '0 0 0.6rem' }}>
								<strong>{preview.wouldCreate}</strong> invoice(s) would be created for{' '}
								{formatPeriodMonth(preview.periodMonth)}, totalling{' '}
								<strong>{formatCents(preview.totalCents, { showCredit: false })}</strong>.
								{preview.wouldSkip > 0 && ` ${preview.wouldSkip} skipped.`}
							</p>
							{preview.wouldSkip > 0 && Array.isArray(preview.skipped) && (
								<details style={{ marginBottom: '0.8rem' }}>
									<summary>Skipped ({preview.wouldSkip})</summary>
									<ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
										{preview.skipped.map((sk) => (
											<li key={sk.invoiceId || sk.familyId}>
												{families.find((f) => f.id === sk.familyId)?.primaryGuardianName ||
													sk.familyId}{' '}
												— {GENERATE_SKIP_LABELS[sk.reason] || sk.reason}
											</li>
										))}
									</ul>
								</details>
							)}

							{preview.wouldCreate > 0 && (
								<>
									<ul style={{ margin: '0 0 0.8rem', paddingLeft: '1.2rem' }}>
										{preview.planned.map((p) => (
											<li key={p.familyId}>
												{families.find((f) => f.id === p.familyId)?.primaryGuardianName ||
													p.familyId}{' '}
												— {p.studentCount} student(s),{' '}
												{formatCents(p.subtotalCents, { showCredit: false })}
												{/* A reissue replaces a VOIDED invoice for the same month and
												    gets its own id. Say so here: otherwise it is indistinguishable
												    from a first issue, and the one thing an admin wants to know
												    before re-issuing is that the void was seen. */}
												{p.reissueOf && (
													<span style={{ color: 'var(--text-muted)' }}>
														{' '}· replaces a cancelled invoice
													</span>
												)}
											</li>
										))}
									</ul>
									{/* The writing action is worded differently from the
									    preview on purpose. "Generate" twice would make the
									    two buttons interchangeable at a glance. */}
									<WaButton variant='brand' loading={busy} disabled={busy} onClick={runForReal}>
										Issue these {preview.wouldCreate} invoice(s)
									</WaButton>
								</>
							)}
						</div>
					)}

					{runResult && (
						<wa-callout variant='success' style={{ marginTop: '1rem' }}>
							<i slot='icon' className='fa-sharp fa-solid fa-circle-check'></i>
							Issued {runResult.created} invoice(s) for{' '}
							{formatPeriodMonth(runResult.periodMonth)}. No cards were charged.
						</wa-callout>
					)}
				</div>

				{/* ── Charge run ──────────────────────────────────────────────── */}
				<div style={cardStyle}>
					<h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', margin: '0 0 0.8rem' }}>
						Charge open invoices
					</h2>
					{/* The mirror of the Generate card's reminder. One card on this
					    page moves money and it is this one; say so where the eye
					    lands before the button does. */}
					<wa-callout variant='warning' style={{ marginBottom: '1rem' }}>
						<i slot='icon' className='fa-sharp fa-solid fa-bolt'></i>
						This is the control that moves money: it debits each family&apos;s
						stored payment method for the month&apos;s open invoice. Preview
						first — the run charges exactly what the preview shows.
					</wa-callout>
					<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
						<select
							value={chargePeriod}
							onChange={(e) => {
								setChargePeriod(e.target.value);
								setChargePlan(null);
								setChargeResult(null);
							}}
							style={{ padding: '0.45rem 0.6rem', borderRadius: 8 }}>
							{MONTH_OPTIONS.map((m) => (
								<option key={m} value={m}>
									{formatPeriodMonth(m)}
								</option>
							))}
						</select>
						<WaButton
							appearance='outlined'
							loading={chargeBusy}
							disabled={chargeBusy}
							onClick={previewCharges}>
							Preview charges
						</WaButton>
					</div>

					{chargePlan && (
						<div style={{ marginTop: '1rem' }}>
							<p style={{ margin: '0 0 0.6rem' }}>
								<strong>{chargePlan.counts.toCharge}</strong> famil
								{chargePlan.counts.toCharge === 1 ? 'y' : 'ies'} would be charged a total of{' '}
								<strong>{formatCents(chargePlan.totalChargeCents, { showCredit: false })}</strong>{' '}
								for {formatPeriodMonth(chargePlan.periodMonth)}.
								{chargePlan.counts.skipped > 0 && ` ${chargePlan.counts.skipped} invoice(s) skipped.`}
							</p>

							{chargePlan.counts.toCharge > 0 && (
								<div className='ps-table-wrap' style={{ marginBottom: '0.8rem' }}>
									<table className='ps-table'>
										<thead>
											<tr>
												<th style={th}>Family</th>
												<th style={th}>Amount</th>
												<th style={th}>Charges to</th>
											</tr>
										</thead>
										<tbody>
											{chargePlan.rows
												.filter((r) => r.action === 'charge')
												.map((r) => (
													<tr key={r.invoiceId}>
														<td style={td}>{r.familyName}</td>
														<td style={td}>
															{formatCents(r.chargeCents, { showCredit: false })}
															{r.creditApplied && (
																<span style={{ color: 'var(--text-muted)' }}> (credit applied)</span>
															)}
														</td>
														<td style={td}>
															{r.target.brand || 'card'} ending {r.target.last4 || '????'}
															{r.target.source === 'portal_consent' && (
																<span style={{ color: 'var(--text-muted)' }}> · added in portal</span>
															)}
														</td>
													</tr>
												))}
										</tbody>
									</table>
								</div>
							)}

							{chargePlan.counts.skipped > 0 && (
								<details style={{ marginBottom: '0.8rem' }}>
									<summary>Skipped ({chargePlan.counts.skipped})</summary>
									<ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
										{chargePlan.rows
											.filter((r) => r.action !== 'charge')
											.map((r) => (
												<li key={r.invoiceId}>
													{r.familyName} — {SKIP_LABELS[r.reason] || r.reason}
												</li>
											))}
									</ul>
								</details>
							)}

							{chargePlan.counts.toCharge > 0 && (
								/* Worded as the money action, matching neither Preview nor
								   Issue — three controls on this page, three verbs. */
								<WaButton variant='danger' loading={chargeBusy} disabled={chargeBusy} onClick={executeCharges}>
									Charge {chargePlan.counts.toCharge} famil
									{chargePlan.counts.toCharge === 1 ? 'y' : 'ies'} —{' '}
									{formatCents(chargePlan.totalChargeCents, { showCredit: false })}
								</WaButton>
							)}
						</div>
					)}

					{chargeResult && (
						<div style={{ marginTop: '1rem' }}>
							<wa-callout
								variant={chargeResult.tally.failed ? 'warning' : 'success'}
								style={{ marginBottom: '0.8rem' }}>
								<i slot='icon' className='fa-sharp fa-solid fa-circle-check'></i>
								Run complete for {formatPeriodMonth(chargeResult.periodMonth)}:{' '}
								{chargeResult.tally.succeeded || 0} charged
								{chargeResult.tally.processing ? `, ${chargeResult.tally.processing} bank debit(s) in flight` : ''}
								{chargeResult.tally.failed ? `, ${chargeResult.tally.failed} FAILED` : ''}.
							</wa-callout>
							<div className='ps-table-wrap'>
								<table className='ps-table'>
									<thead>
										<tr>
											<th style={th}>Family</th>
											<th style={th}>Amount</th>
											<th style={th}>Outcome</th>
										</tr>
									</thead>
									<tbody>
										{chargeResult.results.map((r) => (
											<tr key={r.invoiceId}>
												<td style={td}>{r.familyName || r.familyId}</td>
												<td style={td}>
													{r.chargeCents ? formatCents(r.chargeCents, { showCredit: false }) : '—'}
												</td>
												<td style={td}>
													{OUTCOME_LABELS[r.outcome] || r.outcome}
													{r.errorCode && (
														<span style={{ color: 'var(--text-muted)' }}> ({r.errorCode})</span>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>

				{/* ── Families ────────────────────────────────────────────────── */}
				<div style={cardStyle}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'baseline',
							flexWrap: 'wrap',
							gap: '0.5rem',
							marginBottom: '0.8rem',
						}}>
						<h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', margin: 0 }}>
							Families
						</h2>
						<span style={{ fontSize: '0.85rem', color: 'var(--text-muted, #7b8499)' }}>
							{formatCents(totals.outstanding, { showCredit: false })} outstanding ·{' '}
							{formatPeriodMonth(chargePeriod)}:{' '}
							{readiness.invoiced === 0
								? readiness.generated === 0
									? 'no invoices generated'
									: 'no open invoices'
								: [
										`${readiness.chargeable}/${readiness.invoiced} chargeable`,
										readiness.none > 0 && `${readiness.none} no method`,
										readiness.verifying > 0 && `${readiness.verifying} verifying`,
										readiness.external > 0 && `${readiness.external} external`,
								  ]
										.filter(Boolean)
										.join(' · ')}
						</span>
					</div>

					{loading && <p style={{ margin: 0 }}>Loading…</p>}

					{!loading && (
						<div style={{ overflowX: 'auto' }}>
							<table style={{ width: '100%', borderCollapse: 'collapse' }}>
								<thead>
									<tr>
										<SortHeader
											label='Family'
											columnKey='name'
											sort={sort}
											onSort={toggleSort}
										/>
										<SortHeader
											label='Balance'
											columnKey='balance'
											sort={sort}
											onSort={toggleSort}
										/>
										<SortHeader
											label='Method'
											columnKey='method'
											sort={sort}
											onSort={toggleSort}
										/>
										<th style={th}></th>
									</tr>
								</thead>
								<tbody>
									{rows.map((r) => (
										<React.Fragment key={r.id}>
										<tr
											style={{
												borderTop: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
											}}>
											<td style={td}>{r.primaryGuardianName || r.id}</td>
											<td style={td}>
												{r.balanceCents === null ? '—' : formatCents(r.balanceCents)}
											</td>
											<td style={td} title={r.method.title}>
												<wa-badge variant={r.method.variant}>{r.method.label}</wa-badge>
											</td>
											<td style={td}>
												<WaButton
													appearance='plain'
													onClick={() =>
														setSelectedFamilyId(selectedFamilyId === r.id ? null : r.id)
													}>
													{selectedFamilyId === r.id ? 'Close' : 'Open'}
												</WaButton>
											</td>
										</tr>
										{/* THE DETAIL PANEL RENDERS RIGHT HERE, UNDER THE ROW IT
										    BELONGS TO — not after the table.
										    It used to render after the whole families card, which
										    was fine at 13 families and became invisible at 56: the
										    panel opened roughly 2,500px below the fold, so clicking
										    Open flipped the button label and appeared to do nothing
										    else. Reported that way on 2026-08-21 ("it just changes
										    to a Close button ... not sure where I'm supposed to be
										    doing this").
										    In normal table flow rather than an absolutely-positioned
										    panel, deliberately: same reasoning as TaskQuickBar — no
										    stacking context to lose, nothing to be clipped by an
										    ancestor, and it cannot open off-screen. */}
										{selectedFamilyId === r.id && (
											<tr>
												<td colSpan={4} style={{ padding: '0 0 0.6rem' }}>
													<FamilyBilling
														familyId={r.id}
														family={r}
														cachedBalance={r.balanceCents}
													/>
												</td>
											</tr>
										)}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

/**
 * One family's billing detail: their invoices, their ledger, and the two write
 * actions. Split out so its subscriptions mount and unmount with the selection
 * rather than running for every family on the page at once.
 */
const FamilyBilling = ({ familyId, family, cachedBalance }) => {
	const [invoices, setInvoices] = useState([]);
	const [ledger, setLedger] = useState([]);
	const [busy, setBusy] = useState(false);
	const [msg, setMsg] = useState('');
	const [err, setErr] = useState('');

	const [payAmount, setPayAmount] = useState('');
	// Must name a method the select actually offers. When 'mms' was removed the
	// default could not stay 'mms': the select would fall back to displaying
	// Check while state still held 'mms', so a payment recorded without
	// touching the dropdown would be stored under a method the admin never saw.
	const [payMethod, setPayMethod] = useState('check');
	const [payDate, setPayDate] = useState('');
	const [adjAmount, setAdjAmount] = useState('');
	const [adjReason, setAdjReason] = useState('goodwill');
	const [adjNote, setAdjNote] = useState('');
	const [enrollments, setEnrollments] = useState([]);
	const [students, setStudents] = useState([]);
	// The enrollment the admin is about to hold, or null. Holding it in state
	// (rather than confirm()) is what lets the reason be typed before the write.
	const [withdrawing, setWithdrawing] = useState(null);
	const [withdrawReason, setWithdrawReason] = useState('');

	useEffect(() => {
		const a = subscribeToInvoices(familyId, setInvoices);
		const b = subscribeToLedger(familyId, setLedger);
		const c = subscribeToEnrollments(familyId, setEnrollments);
		const d = subscribeToFamilyStudents(familyId, setStudents);
		return () => {
			a();
			b();
			c();
			d();
		};
	}, [familyId]);

	// Close the hold form when the admin switches families, or it stays open
	// pointing at another family's student.
	useEffect(() => {
		setWithdrawing(null);
		setWithdrawReason('');
	}, [familyId]);

	// Tuition only, this season only — pack/workshop enrollments share the
	// collection and are not billed by the invoice run, so showing them here
	// would imply this control governs them.
	const tuitionEnrollments = useMemo(
		() =>
			enrollments
				.filter((e) => e.type === 'tuition' && e.seasonLabel === season.seasonLabel)
				.sort((a, b) => String(a.studentName || '').localeCompare(String(b.studentName || ''))),
		[enrollments]
	);

	// Students of this family with NO tuition enrollment for the season at all.
	//
	// WHY THIS LIST HAS TO EXIST SEPARATELY: the enrollment list above can only
	// render enrollments, so a student who has never had one is invisible there
	// — and that is precisely the student who is being taught for free. Found
	// 2026-08-21: two trial-converted students held assigned weekly slots with
	// no enrollment, so they appeared on nobody's invoice run.
	//
	// Test students are excluded. They are legitimately unenrolled, they hold
	// slots for QA, and offering "start billing" on one is an invitation to
	// invoice the QA family — the exact hazard card cZxVZY3q describes. The
	// placement script already skips them for the same reason.
	const notEnrolled = useMemo(() => {
		const enrolledIds = new Set(
			enrollments
				.filter((e) => e.type === 'tuition' && e.seasonLabel === season.seasonLabel)
				.map((e) => e.studentId)
		);
		return students
			.filter((s) => !enrolledIds.has(s.id) && s.isTestStudent !== true)
			.sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || '')));
	}, [students, enrollments]);

	const allocated = useMemo(() => allocatePayments(invoices, ledger), [invoices, ledger]);

	const act = useCallback(async (fn, okMsg) => {
		setBusy(true);
		setErr('');
		setMsg('');
		try {
			await fn();
			setMsg(okMsg);
		} catch (e) {
			console.error('[billing-admin]', e);
			setErr(e.message || 'That did not work.');
		} finally {
			setBusy(false);
		}
	}, []);

	const submitPayment = () => {
		const cents = parseDollarsToCents(payAmount);
		if (!cents) {
			setErr('Enter an amount like 240 or 240.00.');
			return;
		}
		act(
			() =>
				recordBillingPayment({
					familyId,
					amountCents: cents,
					paymentMethod: payMethod,
					receivedOn: payDate || null,
					// Keyed on the exact entry being recorded, so a double-submit
					// (or a double-click on a slow connection) records one payment.
					idempotencyKey: `manual-${familyId}-${payDate || 'nodate'}-${cents}-${payMethod}`,
				}),
			`Recorded ${formatCents(cents, { showCredit: false })}.`
		).then(() => setPayAmount(''));
	};

	const submitAdjustment = () => {
		const cents = parseDollarsToCents(String(adjAmount).replace(/^-/, ''));
		if (!cents) {
			setErr('Enter an amount like 25 or 25.00.');
			return;
		}
		// A credit REDUCES what is owed, so it goes in negative. The form takes a
		// plain positive number and the sign is applied here from the direction
		// the reason implies, rather than asking an admin to remember a
		// convention while correcting someone's bill.
		const signed = String(adjAmount).trim().startsWith('-') ? cents : -cents;
		act(
			() =>
				adjustBillingLedger({
					familyId,
					amountCents: signed,
					reason: adjReason,
					note: adjNote || null,
					idempotencyKey: `adj-${familyId}-${adjReason}-${signed}-${adjNote}`,
				}),
			'Adjustment recorded.'
		).then(() => {
			setAdjAmount('');
			setAdjNote('');
		});
	};

	const submitWithdraw = () => {
		// The wa-* `disabled` above is applied as a PROPERTY after the element
		// upgrades, so a click landing before Web Awesome registers still fires.
		// Guard the handler too (CLAUDE.md → Boolean props). Here that guard is
		// load-bearing: without it an empty reason could reach the server, which
		// accepts one, and the record of why would be lost silently.
		const reason = withdrawReason.trim();
		if (busy || !withdrawing || !reason) return;
		const en = withdrawing;
		act(
			() => withdrawTuitionEnrollment({ enrollmentId: en.id, reason }),
			`${en.studentName || 'That student'} will not be invoiced.`
		).then(() => {
			setWithdrawing(null);
			setWithdrawReason('');
		});
	};

	const submitStartBilling = (stu) => {
		if (busy || !stu?.id) return;
		// Creates the first enrollment for this student this season. The callable
		// REFUSES a student with no familyId rather than inventing a payer, so
		// the family binding is a real gate — a failure here is informative, not
		// a bug to route around.
		act(
			() => enrollStudentForSeason({ studentId: stu.id, seasonLabel: season.seasonLabel }),
			`${stu.firstName || 'That student'} will be invoiced from the next run.`
		);
	};

	const submitReEnroll = (en) => {
		if (busy || !en?.studentId) return;
		// Re-enrolling REVIVES this same doc — performEnrollStudentForSeason
		// finds it by (type, studentId, seasonLabel) and flips status back,
		// nulling withdrawnAt/withdrawnReason. One enrollment history per
		// student per season, never a second doc.
		act(
			() => enrollStudentForSeason({ studentId: en.studentId, seasonLabel: en.seasonLabel }),
			`${en.studentName || 'That student'} will be invoiced again.`
		);
	};

	return (
		<div style={{ ...cardStyle, borderColor: 'var(--wa-color-brand-fill-loud, #384971)' }}>
			<h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', margin: '0 0 0.2rem' }}>
				{family?.primaryGuardianName || familyId}
			</h2>
			<p style={{ margin: '0 0 1rem', color: 'var(--text-muted, #7b8499)' }}>
				Balance {cachedBalance === null ? '—' : formatCents(cachedBalance)}
				{family?.primaryGuardianEmail ? ` · ${family.primaryGuardianEmail}` : ''}
			</p>

			{msg && (
				<wa-callout variant='success' style={{ marginBottom: '0.9rem' }}>
					<i slot='icon' className='fa-sharp fa-solid fa-circle-check'></i>
					{msg}
				</wa-callout>
			)}
			{err && (
				<wa-callout variant='danger' style={{ marginBottom: '0.9rem' }}>
					<i slot='icon' className='fa-sharp fa-solid fa-triangle-exclamation'></i>
					{err}
				</wa-callout>
			)}

			<div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
				<div>
					<h3 style={{ fontSize: '0.95rem', margin: '0 0 0.5rem' }}>Record a payment</h3>
					<div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
						<input
							aria-label='Payment amount'
							placeholder='240.00'
							value={payAmount}
							onChange={(e) => setPayAmount(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8, width: 110 }}
						/>
						<select
							aria-label='Payment method'
							value={payMethod}
							onChange={(e) => setPayMethod(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8 }}>
							{PAYMENT_METHODS.map((m) => (
								<option key={m.value} value={m.value}>
									{m.label}
								</option>
							))}
						</select>
						<input
							aria-label='Date received'
							type='date'
							value={payDate}
							onChange={(e) => setPayDate(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8 }}
						/>
						<WaButton variant='brand' loading={busy} disabled={busy} onClick={submitPayment}>
							Record
						</WaButton>
					</div>
				</div>

				<div>
					<h3 style={{ fontSize: '0.95rem', margin: '0 0 0.5rem' }}>Adjust the balance</h3>
					<div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
						<input
							aria-label='Adjustment amount'
							placeholder='25.00'
							value={adjAmount}
							onChange={(e) => setAdjAmount(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8, width: 100 }}
						/>
						<select
							aria-label='Adjustment reason'
							value={adjReason}
							onChange={(e) => setAdjReason(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8 }}>
							{ADJUSTMENT_REASONS.map((r) => (
								<option key={r.value} value={r.value}>
									{r.label}
								</option>
							))}
						</select>
						<input
							aria-label='Adjustment note'
							placeholder='note (optional)'
							value={adjNote}
							onChange={(e) => setAdjNote(e.target.value)}
							style={{ padding: '0.4rem 0.5rem', borderRadius: 8, minWidth: 120 }}
						/>
						<WaButton appearance='outlined' loading={busy} disabled={busy} onClick={submitAdjustment}>
							Apply
						</WaButton>
					</div>
					<p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted, #7b8499)' }}>
						Reduces what they owe. Prefix with a minus to increase it instead.
					</p>
				</div>
			</div>

			{/* ---- Enrollment ----
			    THE ONLY CONTROL ON THIS PAGE THAT CHANGES WHO GETS INVOICED.
			    `performGenerateInvoices` queries enrollments on
			    (type, seasonLabel, status) and reads nothing else — so this
			    section, not the roster and not the schedule, is what decides
			    whether a family appears in next month's run. */}
			<h3 style={{ fontSize: '0.95rem', margin: '1.3rem 0 0.5rem' }}>
				Enrollment · {season.seasonLabel}
			</h3>
			{tuitionEnrollments.length === 0 && notEnrolled.length === 0 ? (
				<p style={{ margin: 0, color: 'var(--text-muted, #7b8499)' }}>
					No tuition enrollment this season — nothing here will be invoiced.
				</p>
			) : tuitionEnrollments.length === 0 ? null : (
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<tbody>
							{tuitionEnrollments.map((en) => {
								const withdrawn = en.status !== 'active';
								return (
									<tr
										key={en.id}
										style={{ borderTop: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))' }}>
										<td style={td}>{en.studentName || en.studentId || '—'}</td>
										<td style={td}>
											<wa-badge variant={withdrawn ? 'neutral' : 'success'}>
												{withdrawn ? 'not billed' : 'billed'}
											</wa-badge>
										</td>
										<td style={{ ...td, color: 'var(--text-muted, #7b8499)', fontSize: '0.85rem' }}>
											{withdrawn ? en.withdrawnReason || 'No reason recorded.' : ''}
										</td>
										<td style={{ ...td, textAlign: 'right' }}>
											{withdrawn ? (
												<WaButton
													size='small'
													disabled={busy}
													onClick={() => submitReEnroll(en)}>
													Re-enroll
												</WaButton>
											) : (
												<WaButton
													size='small'
													variant='neutral'
													disabled={busy}
													onClick={() => setWithdrawing(en)}>
													Hold / withdraw
												</WaButton>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Students of this family who are not enrolled at all. Rendered even
			    when the family HAS enrollments — a two-student family where only
			    one is billed is exactly the case worth surfacing. */}
			{notEnrolled.length > 0 && (
				<div style={{ marginTop: tuitionEnrollments.length ? '0.7rem' : 0 }}>
					<p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: 'var(--text-muted, #7b8499)' }}>
						{notEnrolled.length === 1 ? 'This student is' : 'These students are'} not enrolled
						for {season.seasonLabel} and will not be invoiced.
					</p>
					<div style={{ overflowX: 'auto' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse' }}>
							<tbody>
								{notEnrolled.map((s) => (
									<tr
										key={s.id}
										style={{ borderTop: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))' }}>
										<td style={td}>
											{`${s.firstName || ''} ${s.lastName || ''}`.trim() || s.id}
										</td>
										<td style={td}>
											<wa-badge variant='neutral'>not billed</wa-badge>
										</td>
										<td style={{ ...td, color: 'var(--text-muted, #7b8499)', fontSize: '0.85rem' }}>
											{s.status === 'Active' ? 'Active on the roster' : s.status || ''}
										</td>
										<td style={{ ...td, textAlign: 'right' }}>
											<WaButton size='small' disabled={busy} onClick={() => submitStartBilling(s)}>
												Start billing
											</WaButton>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{withdrawing && (
				<div
					style={{
						marginTop: '0.7rem',
						padding: '0.8rem',
						borderRadius: 10,
						border: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
					}}>
					<p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
						Stop billing <strong>{withdrawing.studentName || withdrawing.studentId}</strong> for{' '}
						{season.seasonLabel}. Their roster status and weekly time slot are not touched — only
						invoicing stops.
					</p>
					{/* The reason is REQUIRED here though the server accepts an empty one.
					    It is the only record of WHY someone was held out of billing, and
					    re-enrolling NULLS it (billingLedger.js) — so if it is not written
					    down at this moment it is not written down anywhere. */}
					<input
						aria-label='Reason for holding this enrollment'
						placeholder='Why? e.g. awaiting family confirmation'
						value={withdrawReason}
						onChange={(e) => setWithdrawReason(e.target.value)}
						style={{ padding: '0.4rem 0.5rem', borderRadius: 8, width: '100%', maxWidth: 420 }}
					/>
					<div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
						<WaButton
							size='small'
							variant='danger'
							disabled={busy || !withdrawReason.trim()}
							onClick={submitWithdraw}>
							Stop billing
						</WaButton>
						<WaButton
							size='small'
							variant='neutral'
							disabled={busy}
							onClick={() => {
								setWithdrawing(null);
								setWithdrawReason('');
							}}>
							Cancel
						</WaButton>
					</div>
				</div>
			)}

			<h3 style={{ fontSize: '0.95rem', margin: '1.3rem 0 0.5rem' }}>Invoices</h3>
			{allocated.length === 0 ? (
				<p style={{ margin: 0, color: 'var(--text-muted, #7b8499)' }}>None yet.</p>
			) : (
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<tbody>
							{allocated.map((inv) => (
								<tr
									key={inv.id}
									style={{ borderTop: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))' }}>
									<td style={td}>{formatPeriodMonth(inv.periodMonth)}</td>
									<td style={td}>{formatCents(inv.subtotalCents, { showCredit: false })}</td>
									<td style={td}>
										<wa-badge
											variant={
												inv.paymentStatus === 'paid'
													? 'success'
													: inv.paymentStatus === 'void'
													? 'neutral'
													: 'warning'
											}>
											{inv.paymentStatus === 'void' ? 'cancelled' : inv.paymentStatus}
										</wa-badge>
									</td>
									<td style={td}>
										{inv.status !== 'void' && (
											<WaButton
												appearance='plain'
												disabled={busy}
												onClick={() =>
													act(
														() => voidInvoice({ invoiceId: inv.id, reason: 'admin void' }),
														'Invoice cancelled and reversed.'
													)
												}>
												Cancel
											</WaButton>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<h3 style={{ fontSize: '0.95rem', margin: '1.3rem 0 0.5rem' }}>Ledger</h3>
			{ledger.length === 0 ? (
				<p style={{ margin: 0, color: 'var(--text-muted, #7b8499)' }}>No entries.</p>
			) : (
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<tbody>
							{ledger.map((e) => (
								<tr
									key={e.id}
									style={{ borderTop: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))' }}>
									<td style={td}>
										{e.receivedOn
											? formatDateOnly(e.receivedOn)
											: e.createdAt?.toDate
											? e.createdAt.toDate().toLocaleDateString()
											: '—'}
									</td>
									<td style={td}>
										{describeLedgerEntry(e)}
										{e.note ? ` — ${e.note}` : ''}
									</td>
									<td style={td}>
										{e.amountCents > 0 ? '+' : '−'}
										{formatCents(Math.abs(e.amountCents || 0), { showCredit: false })}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<div style={{ marginTop: '1rem' }}>
				<WaButton
					appearance='plain'
					disabled={busy}
					onClick={() =>
						act(async () => {
							const r = await reconcileBillingBalance({ familyId, repair: true });
							setMsg(
								r.drifted
									? `Balance repaired: was ${formatCents(r.cachedBalanceCents)}, now ${formatCents(r.trueBalanceCents)}.`
									: 'Balance already matches the ledger.'
							);
						}, '')
					}>
					Check balance against the ledger
				</WaButton>
			</div>
		</div>
	);
};

export default Billing;
